import { useMutation, useQuery, useQueryClient } from 'react-query';
import type { UseMutationResult, UseQueryResult } from 'react-query';

import API from '../../../utils/api';
import { ClientService } from '../../../shared/api/ClientService';
import { INTEGRATION_MONITORING_QUERY, USE_QUERY_OPTIONS } from '../../../constants/reactQuery';
import { COMMAND_QUERY_KEY, COMMANDS_QUERY_KEY, EVENTS_QUERY_KEY, SUMMARY_QUERY_KEY } from './constants';
import type { CommandPage, EventPage, ICommandListParams, IEventListParams } from './types';

// A failed load is shown with a Retry link instead of being retried silently: a 403 does not heal.
// Every mount reads again: an operations page must not show cached numbers.
const QUERY_OPTIONS = { ...USE_QUERY_OPTIONS, retry: false, refetchOnMount: 'always' as const };

type ActionMutation = UseMutationResult<ClientService.IntegrationActionResultDto, unknown, string[]>;
type ReplayMutation = UseMutationResult<ClientService.ReplayEventsResultDto, unknown, string[]>;

export const useIntegrationSummary = (): UseQueryResult<ClientService.IntegrationSummaryDto> =>
  useQuery([INTEGRATION_MONITORING_QUERY, SUMMARY_QUERY_KEY], () => API.getSummary(), QUERY_OPTIONS);

export const useIntegrationCommands = (params: ICommandListParams): UseQueryResult<CommandPage> =>
  useQuery(
    [INTEGRATION_MONITORING_QUERY, COMMANDS_QUERY_KEY, params],
    () =>
      API.getCommandList(
        params.status,
        params.commandType,
        params.fileName,
        params.createdFrom,
        params.createdTo,
        params.heldOnly,
        params.sorting,
        params.skipCount,
        params.maxResultCount
      ),
    { ...QUERY_OPTIONS, keepPreviousData: true }
  );

export const useIntegrationEvents = (params: IEventListParams): UseQueryResult<EventPage> =>
  useQuery(
    [INTEGRATION_MONITORING_QUERY, EVENTS_QUERY_KEY, params],
    () =>
      API.getEventList(
        params.status,
        params.eventType,
        params.occurredFrom,
        params.occurredTo,
        params.sorting,
        params.skipCount,
        params.maxResultCount
      ),
    { ...QUERY_OPTIONS, keepPreviousData: true }
  );

export const useIntegrationCommand = (
  id: string | undefined,
  enabled: boolean
): UseQueryResult<ClientService.IntegrationCommandDto> =>
  useQuery([INTEGRATION_MONITORING_QUERY, COMMAND_QUERY_KEY, id], () => API.getCommand(id), {
    ...QUERY_OPTIONS,
    enabled: enabled && !!id,
  });

const useActionMutation = (
  send: (input: ClientService.IntegrationCommandIdsInput) => Promise<ClientService.IntegrationActionResultDto>
): ActionMutation => {
  const queryClient = useQueryClient();

  return useMutation((ids: string[]) => send(new ClientService.IntegrationCommandIdsInput({ ids })), {
    // An action changes commands and the counts of the summary, not the events.
    onSettled: () => {
      queryClient.invalidateQueries([INTEGRATION_MONITORING_QUERY, SUMMARY_QUERY_KEY]);
      queryClient.invalidateQueries([INTEGRATION_MONITORING_QUERY, COMMANDS_QUERY_KEY]);
      queryClient.invalidateQueries([INTEGRATION_MONITORING_QUERY, COMMAND_QUERY_KEY]);
    },
  });
};

export const useRetryCommands = (): ActionMutation => useActionMutation((input) => API.retryCommands(input));

export const useCancelCommands = (): ActionMutation => useActionMutation((input) => API.cancelCommands(input));

// A replay is asynchronous: the events come back within seconds, so these two reads may still be unchanged.
export const useReplayEvents = (): ReplayMutation => {
  const queryClient = useQueryClient();

  return useMutation((eventIds: string[]) => API.replayEvents(new ClientService.ReplayEventsInput({ eventIds })), {
    onSuccess: () => {
      queryClient.invalidateQueries([INTEGRATION_MONITORING_QUERY, EVENTS_QUERY_KEY]);
      queryClient.invalidateQueries([INTEGRATION_MONITORING_QUERY, SUMMARY_QUERY_KEY]);
    },
  });
};
